import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SecurityContext,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { RestrictInputDirective } from '@directives/restrict-input/restrict-input.directive';
import {
  FieldGroupMetaData,
  FieldMetaData,
  FieldsData,
  FileDelete,
  FileUpload
} from '@models/checkout.model';
import { loadPaymentCardImage } from '@models/functions.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { DatePickerComponent } from '@shared-components/custom-fields/date-picker/date-picker.component';
import { DeviceSelectComponent } from '@shared-components/custom-fields/device-select/device-select.component';
import { LineSelectComponent } from '@shared-components/custom-fields/line-select/line-select.component';
import { PersonSelectComponent } from '@shared-components/custom-fields/person-select/person-select.component';
import {
  CONDITIONAL_OFFSET,
  CUSTOM_DIRECTIVE,
  CUSTOM_DIVIDERS,
  CUSTOM_FIELD_STYLES,
  EMPTY_READONLY_TEMPLATES,
  HIDE_SELECT_ONE_OPTION,
  READONLY_LABEL
} from '@utils/custom-form.utils';
import { Subscription } from 'rxjs';
import { DataLoaderDirective, DomSanitizerPipe } from 'tangoe-design-system';

@Component({
  selector: 'ts-custom-form',
  templateUrl: './custom-form.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DataLoaderDirective,
    DomSanitizerPipe,
    TranslationPipe,
    DatePickerComponent,
    DeviceSelectComponent,
    LineSelectComponent,
    PersonSelectComponent,
    RestrictInputDirective
  ]
})
export class CustomFormComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @Input() form: FormGroup;
  @Input() json_form_data: FieldsData['groups'];
  @Output() no_new_carrier_accounts: EventEmitter<boolean> = new EventEmitter();
  divider: { [k: string]: string[] } = {};
  private subscriptions: Subscription = new Subscription();

  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private shopping_svc: ShoppingService,
    private activity_manager_svc: ActivityManagerService
  ) {
    this.divider = CUSTOM_DIVIDERS;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['json_form_data'] && [19].includes(this.activity_manager_svc.currentOrderTypeId()))
      this.checkNoNewCarrierAccounts();
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onFileChange(event: any, field: FieldMetaData, control: FormControl): void {
    if (event.target.files && event.target.files.length) {
      this.subscriptions.add(
        this.shopping_svc.uploadFile(field.id, event.target.files).subscribe({
          next: (res: FileUpload) => {
            control.patchValue(event.target.files[0].name);
            field.value = res.original_file_name;
          },
          error: (err: HttpErrorResponse) => {}
        })
      );
    }
  }

  deleteFile(field: FieldMetaData): void {
    this.subscriptions.add(
      this.shopping_svc.deleteFile(field.id).subscribe({
        next: (res: FileDelete) => {
          field.value = null;
          field.control.reset();
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  printAgreement(group: FieldGroupMetaData, value: string): void {
    let popupWindow: Window;
    const agreement_information: FieldMetaData[] = group.fields.filter(
      (field: FieldMetaData) => field.name.indexOf('agreement_information_') > -1
    );

    popupWindow = window.open(
      '',
      '_blank',
      'width=900,height=700,scrollbars=no,menubar=no,toolbar=no,location=no,status=no,titlebar=no'
    )!;
    popupWindow.document.open();
    popupWindow.document.write(
      `<h3>${this.sanitize(this.getFieldValue('agreement_name', group))}</h3>`
    );
    popupWindow.document.write(`${this.sanitize(value)}`);
    agreement_information.forEach((info: FieldMetaData) =>
      popupWindow.document.write(`<div>${this.sanitize(info.label)}</div>`)
    );
    setTimeout(() => {
      popupWindow.print();
    }, 500);
  }

  readOnlyTemplate(field: FieldMetaData, group: FieldGroupMetaData): string {
    if (EMPTY_READONLY_TEMPLATES[group.name]?.includes(field.name)) {
      return ``;
    }

    return `<div>${field.display_name ? field.display_name : field.value}</div>`;
  }

  sanitize(value: string): string {
    return this.sanitizer.sanitize(
      SecurityContext.HTML,
      this.sanitizer.bypassSecurityTrustHtml(value)
    )!;
  }

  sortFields(fields: FieldMetaData[]): FieldMetaData[] {
    return fields.sort((a: FieldMetaData, b: FieldMetaData) =>
      a.display_sort_order > b.display_sort_order
        ? 1
        : b.display_sort_order > a.display_sort_order
          ? -1
          : 0
    );
  }

  splitColumns(group: FieldGroupMetaData, field: FieldMetaData): string {
    const baseStyle: string = CUSTOM_FIELD_STYLES[group.name]?.[field.name] || '';

    const offsetRule: { dependsOn: string; class: string } | undefined =
      CONDITIONAL_OFFSET[field.name];

    if (offsetRule) {
      const dependencyExists: boolean =
        group.fields?.some((f: FieldMetaData) => f.name === offsetRule.dependsOn) ?? false;

      if (dependencyExists) return `col-12 ${baseStyle} ${offsetRule.class}`.trim();
    }

    return `col-12 ${baseStyle}`.trim();
  }

  getPaymentCardImage(card_type: string): string {
    return loadPaymentCardImage(card_type);
  }

  getAddressLabel(group: FieldGroupMetaData): string {
    const field_presence: boolean = !!!group.fields.find((field: FieldMetaData) =>
      ['shipping_address_select', 'preferred_area_location'].includes(field.name)
    );

    return field_presence && READONLY_LABEL[group.name] ? READONLY_LABEL[group.name] : '';
  }

  getFieldValue(field_name: string, group: FieldGroupMetaData): string {
    return (
      (group.fields.find((field: FieldMetaData) => field.name === field_name)?.value as string) ??
      ``
    );
  }

  getTextDirective(field_name: string, group: FieldGroupMetaData): string | Array<string> {
    return CUSTOM_DIRECTIVE[group.name]?.[field_name] ?? 'no_empty_space';
  }

  hideSelectOneOption(field_name: string, group: FieldGroupMetaData): boolean {
    return HIDE_SELECT_ONE_OPTION[group.name]?.includes(field_name);
  }

  showSelectField(field_name: string, group: FieldGroupMetaData): boolean {
    return field_name === 'new_carrier_account' ? this.hasNewCarrierAccounts(group) : true;
  }

  hasNewCarrierAccounts(group: FieldGroupMetaData): boolean {
    return group.fields.some(
      (field: FieldMetaData) =>
        field.name === 'new_carrier_account' && field.options && field.options?.length > 0
    );
  }

  private checkNoNewCarrierAccounts(): void {
    const has_no_new_carrier_accounts: boolean = !!this.json_form_data?.some(
      (group: FieldGroupMetaData) =>
        group.name === 'carrier_account' &&
        group.fields?.some(
          (field: FieldMetaData) =>
            field.name === 'new_carrier_account' && field.options?.length === 0
        )
    );

    this.no_new_carrier_accounts.emit(has_no_new_carrier_accounts);
  }
}
